import { type ReactNode } from 'react';
import styles from './connection-line.module.css';

export type ConnectionDirection = 'up' | 'down';

/** Uç noktasının yarıçapı: 7 px çaplı nokta (ConnectionLine kartı). */
export const CONNECTION_END_RADIUS = 3.5;
/** Uç noktasının kutu kenarına uzaklığı (tasarımın `bond` üreticisi, `d = 4.5`). */
const END_INSET = 4.5;

export interface ConnectionGeometry {
  readonly path: string;
  readonly start: readonly [number, number];
  readonly end: readonly [number, number];
}

/**
 * Bağ çizgisinin geometrisi — tasarımın `WarmBreak.bond(w, h, dir)` üreticisiyle
 * aynı formül. `down`: uçlar üstte, kavis aşağı sarkar (ana sayfadaki üç kişilik
 * düzen). `up`: kavis yukarı kalkar (yatay eşleşme).
 */
export function connectionGeometry(
  width: number,
  height: number,
  direction: ConnectionDirection,
): ConnectionGeometry {
  const y0 = direction === 'up' ? height - END_INSET : END_INSET;
  const yc = direction === 'up' ? -height * 0.3 : height * 1.3;
  const round = (value: number) => Math.round(value * 100) / 100;
  return {
    path: `M${END_INSET} ${round(y0)} C ${round(width * 0.18)} ${round(yc)}, ${round(width * 0.82)} ${round(yc)}, ${round(width - END_INSET)} ${round(y0)}`,
    start: [END_INSET, round(y0)],
    end: [round(width - END_INSET), round(y0)],
  };
}

export interface ConnectionLineProps {
  readonly width: number;
  readonly height: number;
  readonly direction?: ConnectionDirection;
  readonly className?: string;
}

/**
 * BAĞ ÇİZGİSİ (ConnectionLine kartı) — iki kişiyi birleştiren ince mandalina
 * kavis, uçlarında 7 px noktalar. Çizgi `accent`, kalınlık `bond-stroke` (2 px).
 *
 * YALNIZ BİR BAĞI ANLATTIĞI YERDE: kişi önerisinin gerekçesi, kabul edilen
 * görüşme, iki tarafın onayladığı kartvizit, cüzdan, tanışma ile ilgili boş
 * ekran. Süs, ayırıcı, desen ya da yükleniyor çizgisi DEĞİLDİR. Çizgi tek
 * başına bilgi taşımaz (açık temada `accent` beyaza 2,6:1), bu yüzden her
 * zaman dekoratiftir (`aria-hidden`); anlamı yanındaki avatar ve metin verir.
 */
export function ConnectionLine({
  width,
  height,
  direction = 'up',
  className,
}: ConnectionLineProps) {
  const geometry = connectionGeometry(width, height, direction);
  return (
    <span
      className={[styles.bond, className ?? ''].filter(Boolean).join(' ')}
      data-connection-line={direction}
    >
      <svg
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
      >
        <path className={styles.path} d={geometry.path} />
        <circle
          className={styles.end}
          cx={geometry.start[0]}
          cy={geometry.start[1]}
          r={CONNECTION_END_RADIUS}
        />
        <circle
          className={styles.end}
          cx={geometry.end[0]}
          cy={geometry.end[1]}
          r={CONNECTION_END_RADIUS}
        />
      </svg>
    </span>
  );
}

export interface ConnectionPairProps {
  /** Sol uçtaki kişi (çağıran `Avatar ring` verir). */
  readonly start: ReactNode;
  /** Sağ uçtaki kişi. */
  readonly end: ReactNode;
  readonly width: number;
  readonly height: number;
  readonly className?: string;
}

/**
 * İKİ KİŞİ VE ARALARINDAKİ BAĞ (`wb-pair`): uçlar avatarların kenarına değer.
 * Yatay eşleşmede kavis yukarı kalkar.
 */
export function ConnectionPair({ start, end, width, height, className }: ConnectionPairProps) {
  return (
    <span className={[styles.pair, className ?? ''].filter(Boolean).join(' ')}>
      {start}
      <ConnectionLine className={styles.pairLine} width={width} height={height} direction="up" />
      {end}
    </span>
  );
}
